import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { API_URL, TOKEN_KEY, storage } from '../../api/client';
import { syncRiderLocation, LOCATION_ERRORS } from '../../services/riderLocation';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { Badge, Button, Loading, Modal } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { colors } from '../../theme';
import { currency } from '../../utils/format';
import { isRoad } from '../../utils/partnerTypes';
import { kindsOf, sidesOf } from '../../utils/vertical';

/* What the business sells decides its first shortcuts. */
const quickFor = (partner, master = {}) => {
  const SELL = {
    food: [{ icon: '🍽️', label: 'Add item', to: '/partner/catalogue/new?kind=product' }, { icon: '📋', label: 'Pick from master menu', to: '/partner/master-menu?type=dish' }],
    hotel: [{ icon: '🛏️', label: 'Add room', to: '/partner/hotel' }, { icon: '📅', label: 'Room bookings', to: '/partner/hotel' }],
    travel: [{ icon: '🧳', label: 'Add service', to: '/partner/catalogue/new?kind=service' }, { icon: '📋', label: 'Pick from master list', to: '/partner/master-menu?type=service' }, { icon: '📅', label: 'Bookings', to: '/partner/bookings' }],
    garage: [{ icon: '🔧', label: 'Add service', to: '/partner/catalogue/new?kind=service' }, { icon: '📋', label: 'Pick from master list', to: '/partner/master-menu?type=service' }, { icon: '📅', label: 'Bookings', to: '/partner/bookings' }],
    service: [{ icon: '🛠️', label: 'Add service', to: '/partner/catalogue/new?kind=service' }, { icon: '📋', label: 'Pick from master list', to: '/partner/master-menu?type=service' }, { icon: '📅', label: 'Bookings', to: '/partner/bookings' }],
    showroom: [{ icon: '📦', label: 'Add product', to: '/partner/catalogue/new?kind=product' }, { icon: '📋', label: 'Pick from master list', to: '/partner/master-menu?type=product' }, { icon: '🎥', label: '360° showroom', to: '/partner/showroom-tour' }],
    shop: [{ icon: '📦', label: 'Add product', to: '/partner/catalogue/new?kind=product' }, { icon: '📋', label: 'Pick from master list', to: '/partner/master-menu?type=product' }, { icon: '🛠️', label: 'Add service', to: '/partner/catalogue/new?kind=service' }],
    hiring: [],
  };
  // Every side the business has, main one first; a shortcut is never listed twice.
  const sell = [];
  const listType = (to) => (String(to).match(/type=(\w+)/) || [])[1];
  kindsOf(partner).forEach((k) => (SELL[k] || []).forEach((q) => {
    // A master-list shortcut only when admin has entries for this business.
    const t = listType(q.to);
    if (t && !master[t]) return;
    if (!sell.some((s) => s.label === q.label)) sell.push(q);
  }));
  const shelves = sell.length ? [{ icon: '🗂️', label: 'My categories', to: '/partner/categories' }] : [];
  return [
    ...sell,
    ...shelves,
    { icon: '💼', label: 'Post a job', to: '/partner/jobs/new' },
    { icon: '🧑‍🔧', label: 'Hire people', to: '/people' },
    { icon: '🏪', label: 'My business', to: '/partner/business' },
  ];
};

/*
 * Someone who works on the road has no catalogue and hires nobody - their day
 * is the bookings in front of them, so that is the only shortcut they get.
 */
const RIDER_QUICK = [
  { icon: '📅', label: "Today's bookings", to: '/partner/bookings' },
];

/** Partner home: approval state, live counters and quick actions. */
const ago = (iso) => {
  if (!iso) return 'never';
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso)) / 1000));
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)} min ago`;
  return `${Math.round(sec / 3600)} h ago`;
};

/**
 * "Where KAAMWALA thinks you are" for a delivery partner: the position the
 * server holds (what dispatch rings from), its accuracy and age, a refetch
 * button, and a map to see the pin itself.
 */
const RiderLocationCard = ({ location, syncing, error, onRefetch }) => {
  const [mapOpen, setMapOpen] = useState(false);
  const c = location && location.coordinates;
  const token = storage.get(TOKEN_KEY);
  const mapUrl = c && token
    ? `${API_URL}/maps/static?center=${c[0]},${c[1]}&pin=${c[0]},${c[1]}&zoom=16&w=640&h=400&token=${token}`
    : null;
  const lowAccuracy = location && location.accuracy && location.accuracy > 100;

  /*
   * The map popup is a SIBLING of the card, not a child: Reveal animates with a
   * CSS transform, and a transformed ancestor traps position:fixed descendants
   * inside it - the popup rendered clipped within the card instead of over
   * the page.
   */
  return (
    <>
    <Reveal className="card sh-sm" style={{ marginBottom: 16 }}>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 22 }}>📍</span>
        <div className="grow">
          <div className="t-tiny" style={{ fontWeight: 800, letterSpacing: 0.6 }}>YOUR CURRENT LOCATION</div>
          <div className="t-title">
            {syncing && !location ? 'Finding you…' : ((location && location.city) || (c ? 'Location saved' : 'Not set yet'))}
          </div>
        </div>
        <button type="button" className="btn ghost btn-sm" onClick={onRefetch} disabled={syncing} title="Refetch my location">
          {syncing ? '…' : '🔄 Refetch'}
        </button>
      </div>
      {location && location.address ? <div className="t-small" style={{ marginTop: 6 }}>{location.address}</div> : null}
      {c ? (
        <div className="t-tiny" style={{ marginTop: 4 }}>
          {c[1].toFixed(5)}, {c[0].toFixed(5)}
          {location.accuracy ? ` · ±${location.accuracy} m` : ''}
          {` · synced ${ago(location.updatedAt)}`}
        </div>
      ) : null}
      {error ? (
        <div className="t-tiny" style={{ marginTop: 8, color: colors.danger, background: colors.dangerBg, padding: 8, borderRadius: 8 }}>
          {LOCATION_ERRORS[error] || error}
        </div>
      ) : lowAccuracy ? (
        <div className="t-tiny" style={{ marginTop: 8, color: colors.warning, background: colors.warningBg, padding: 8, borderRadius: 8 }}>
          This fix is only accurate to about {location.accuracy} m. Refetch for a sharper one.
        </div>
      ) : location && location.radiusKm ? (
        <div className="t-tiny" style={{ marginTop: 6, color: colors.success, fontWeight: 700 }}>
          ✓ Saved - orders within {location.radiusKm} km of this point will ring you.
        </div>
      ) : null}
      {c ? (
        <button type="button" className="btn ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setMapOpen(true)}>
          🗺️ View on map
        </button>
      ) : null}

    </Reveal>
      <Modal open={mapOpen} title="Your pin on the map" onClose={() => setMapOpen(false)} wide>
        <div className="t-tiny" style={{ marginBottom: 8 }}>This is exactly where KAAMWALA sends you orders from.</div>
        {mapUrl ? <img src={mapUrl} alt="Your location" style={{ width: '100%', borderRadius: 12, display: 'block' }} /> : null}
        {location && location.address ? <div className="t-small" style={{ marginTop: 8 }}>{location.address}</div> : null}
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="btn ghost" onClick={onRefetch} disabled={syncing}>{syncing ? 'Refetching…' : '🔄 Refetch'}</button>
          {c ? (
            <a className="btn ghost" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${c[1]},${c[0]}`}>🧭 Open in Google Maps</a>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const { user, partner, setPartner } = useAuth();
  const { refreshUnread } = useApp();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Delivery partners are rung by the position the server holds - keep it fresh.
  const isRider = isRoad(partner);
  const [master, setMaster] = useState({});
  useEffect(() => { api.menu.availability().then((r) => setMaster(r.data || {})).catch(() => setMaster({})); }, []);
  // Counters in the trade's words: a shop has orders, a tour operator has
  // bookings, a hotel has stays and rooms - never an "Orders today" for a hotel.
  const sides = sidesOf(partner);
  const foodSide = sides.menu;
  const st = stats || {};
  const todayStats = [
    sides.products ? { value: (st.orders && st.orders.today) || 0, label: 'Orders today' } : null,
    sides.services || isRider ? { value: (st.bookings && st.bookings.today) || 0, label: 'Bookings today' } : null,
    sides.rooms ? { value: (st.stays && st.stays.today) || 0, label: 'Stays today' } : null,
    { value: `⭐ ${st.rating || 0}`, label: `${st.ratingCount || 0} reviews` },
  ].filter(Boolean).slice(-3);
  const counters = [
    sides.products ? { label: 'Pending orders', value: (st.orders && st.orders.pending) || 0, icon: '🧾', color: colors.accent, to: '/partner/orders' } : null,
    sides.services || isRider ? { label: 'Pending bookings', value: (st.bookings && st.bookings.pending) || 0, icon: '📅', color: colors.primary, to: '/partner/bookings' } : null,
    sides.rooms ? { label: 'Room bookings', value: (st.stays && st.stays.pending) || 0, icon: '🛎️', color: colors.primary, to: '/partner/hotel?tab=bookings' } : null,
    sides.products ? { label: foodSide ? 'Menu items' : 'Products', value: (st.catalogue && st.catalogue.products) || 0, icon: foodSide ? '🍽️' : '📦', color: '#7C3AED', to: '/partner/catalogue' } : null,
    sides.services ? { label: 'Services', value: (st.catalogue && st.catalogue.services) || 0, icon: '🛠️', color: colors.success, to: '/partner/catalogue?tab=services' } : null,
    sides.rooms ? { label: 'Rooms', value: (st.catalogue && st.catalogue.rooms) || 0, icon: '🛏️', color: '#7C3AED', to: '/partner/hotel?tab=rooms' } : null,
  ].filter(Boolean);
  const [myLoc, setMyLoc] = useState(null);
  const [locSyncing, setLocSyncing] = useState(false);
  const [locError, setLocError] = useState(null);
  const synced = useRef(false);

  const refetchLocation = useCallback(async ({ fresh = true } = {}) => {
    setLocSyncing(true);
    setLocError(null);
    try {
      const res = await syncRiderLocation({ fresh });
      if (res.error) setLocError(res.error); else setMyLoc(res);
      return res;
    } finally { setLocSyncing(false); }
  }, []);

  // Opening the dashboard re-reads the position once.
  useEffect(() => {
    if (!isRider || synced.current || !partner || partner.status !== 'approved') return;
    synced.current = true;
    refetchLocation({ fresh: false });
  }, [isRider, partner, refetchLocation]);

  const load = useCallback(() => {
    api.partners.dashboard()
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => { load(); }, [load]);

  // The counters are the partner's working screen - keep them fresh.
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const toggleOpen = async (value) => {
    setToggling(true);
    try {
      /*
       * Switch first, then locate. Waiting for a fresh fix before sending the
       * switch froze the toggle for up to 20 s on weak GPS, off duty all the
       * while. The refetch that follows pushes the position itself.
       */
      const res = await api.partners.toggleOpen({ isOpen: value });
      setPartner({ ...partner, isOpen: res.data.isOpen });
      setStats((s) => (s ? { ...s, isOpen: res.data.isOpen } : s));
      if (value && isRider) refetchLocation({ fresh: true });
    } catch (e) { /* ignore */ } finally { setToggling(false); }
  };

  if (loading) return <Loading text="Loading your dashboard…" />;

  const pending = stats && stats.status !== 'approved';

  return (
    <div>
      <Reveal className="row-between wrap" style={{ gap: 14, marginBottom: 18 }}>
        <div>
          <div className="t-small">Welcome back</div>
          <h1 className="h2">{(partner && partner.businessName) || (user && user.name)}</h1>
        </div>

        {!pending ? (
          <button
            type="button"
            onClick={() => toggleOpen(!(stats && stats.isOpen))}
            disabled={toggling}
            className="press"
            style={{
              display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
              background: stats && stats.isOpen ? colors.successBg : colors.dangerBg,
              border: `1px solid ${stats && stats.isOpen ? '#BBEBCD' : '#F7CFCF'}`,
              borderRadius: 16, padding: '10px 14px',
            }}
          >
            <span>
              <span
                className="t-title"
                style={{ display: 'block', color: stats && stats.isOpen ? colors.success : colors.danger }}
              >
                {stats && stats.isOpen ? '🟢 You are OPEN' : '🔴 You are CLOSED'}
              </span>
              <span className="t-tiny">
                {stats && stats.isOpen
                  ? 'Customers can order and book right now'
                  : 'You will not receive new orders'}
              </span>
            </span>
            <span
              style={{
                width: 44, height: 26, borderRadius: 13, flex: '0 0 auto',
                background: stats && stats.isOpen ? colors.success : colors.line,
                position: 'relative', transition: 'background .2s ease',
              }}
            >
              <span
                style={{
                  position: 'absolute', top: 3, left: stats && stats.isOpen ? 21 : 3,
                  width: 20, height: 20, borderRadius: 10, background: '#fff',
                  transition: 'left .2s cubic-bezier(.22,1,.36,1)',
                  boxShadow: '0 1px 4px rgba(0,0,0,.2)',
                }}
              />
            </span>
          </button>
        ) : null}
      </Reveal>

      {isRider && !pending ? (
        <RiderLocationCard
          location={myLoc}
          syncing={locSyncing}
          error={locError}
          onRefetch={() => refetchLocation({ fresh: true })}
        />
      ) : null}

      {pending ? (
        <Reveal className="card sh-sm" style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 34 }}>{stats.status === 'rejected' ? '❌' : '⏳'}</div>
          <h3 className="h3" style={{ marginTop: 8 }}>
            {stats.status === 'rejected' ? 'Application rejected' : 'Approval pending'}
          </h3>
          <p className="t-small" style={{ marginTop: 6, lineHeight: 1.6, maxWidth: 460, margin: '6px auto 0' }}>
            {stats.status === 'rejected'
              ? (partner && partner.rejectionReason) || 'Please review your details and resubmit.'
              : 'Our team is reviewing your business. You can add products and services as soon as it is approved — usually within 24 hours.'}
          </p>
          <Button
            title="Review my details"
            style={{ marginTop: 14 }}
            onClick={() => navigate('/partner/business')}
          />
        </Reveal>
      ) : null}

      {/* Earnings */}
      <Reveal
        delay={60}
        className="card sh-md"
        style={{
          background: `linear-gradient(120deg, ${colors.primary}, ${colors.primaryDark})`,
          border: 0, color: '#fff', padding: 22,
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, opacity: 0.75 }}>
          TOTAL EARNINGS
        </div>
        <div style={{ fontSize: 34, fontWeight: 900, marginTop: 4 }}>
          {currency((stats && stats.earnings) || 0)}
        </div>

        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginTop: 18 }}
        >
          {todayStats.map((s) => (
            <div
              key={s.label}
              style={{ background: 'rgba(255,255,255,.14)', borderRadius: 14, padding: '12px 14px' }}
            >
              <div style={{ fontSize: 19, fontWeight: 800 }}>{s.value}</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Counters */}
      <div className="grid grid-stats" style={{ marginTop: 16 }}>
        {counters.map((c, i) => (
          <Reveal key={c.label} delay={80 + i * 55}>
            <button
              type="button"
              onClick={() => navigate(c.to)}
              className="card sh-sm press hoverable"
              style={{ width: '100%', textAlign: 'left' }}
            >
              <span
                style={{
                  width: 38, height: 38, borderRadius: 11, background: `${c.color}18`,
                  display: 'grid', placeItems: 'center', fontSize: 18,
                }}
              >
                {c.icon}
              </span>
              <div style={{ fontSize: 26, fontWeight: 900, color: c.color, marginTop: 10 }}>{c.value}</div>
              <div className="t-tiny">{c.label}</div>
            </button>
          </Reveal>
        ))}
      </div>

      {/* Quick actions */}
      <h3 className="h3" style={{ marginTop: 28, marginBottom: 12 }}>Quick actions</h3>
      <div className="grid grid-stats">
        {(isRider ? RIDER_QUICK : quickFor(partner, master)).map((q, i) => (
          <Reveal key={q.to} delay={stagger(i, 50)}>
            <button
              type="button"
              onClick={() => navigate(q.to)}
              disabled={pending && q.to !== '/partner/business'}
              className="card sh-sm press hoverable"
              style={{
                width: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 8, padding: 18,
                opacity: pending && q.to !== '/partner/business' ? 0.5 : 1,
              }}
            >
              <span style={{ fontSize: 24 }}>{q.icon}</span>
              <span className="t-body" style={{ fontWeight: 700 }}>{q.label}</span>
            </button>
          </Reveal>
        ))}
      </div>

      {/* Totals */}
      <Reveal delay={200} className="card sh-sm" style={{ marginTop: 24 }}>
        <h3 className="t-title" style={{ marginBottom: 12 }}>All time</h3>
        {[
          ['Total orders', String((stats && stats.orders && stats.orders.total) || 0)],
          ['Total bookings', String((stats && stats.bookings && stats.bookings.total) || 0)],
        ].map(([label, value]) => (
          <div key={label} className="row-between" style={{ padding: '8px 0' }}>
            <span className="t-small">{label}</span>
            <span className="t-title">{value}</span>
          </div>
        ))}
        <div className="row-between" style={{ padding: '8px 0' }}>
          <span className="t-small">Business status</span>
          <Badge value={stats && stats.status} />
        </div>
      </Reveal>
    </div>
  );
};

export default PartnerDashboardPage;
